import { SearchIndexItem } from "@/types/help.types.ts";
import { useDebouncedSearchParam } from "@skailer-com/uikit";
import { useMemo } from "react";

interface UseHelpSearchOptions {
  searchIndex: SearchIndexItem[] | null;
  minQueryLength?: number;
  maxResults?: number;
}

interface SearchResult extends SearchIndexItem {
  score: number;
  matchType: "title" | "description" | "content";
}

const tokenizeSearchText = (text: string): string[] =>
  text
    .toLowerCase()
    .replaceAll("ё", "е")
    .match(/[\p{L}\p{N}]+/gu) || [];

const stemEnglishToken = (token: string): string => {
  if (token.length <= 4) return token;

  const suffixes = ["ingly", "edly", "ing", "ed", "es", "s"];
  const matchedSuffix = suffixes.find((suffix) => token.endsWith(suffix));

  if (!matchedSuffix) {
    return token;
  }

  const stem = token.slice(0, -matchedSuffix.length);
  return stem.length >= 3 ? stem : token;
};

const stemRussianToken = (token: string): string => {
  if (token.length <= 4) return token;

  const suffixes = [
    "иями",
    "ями",
    "ами",
    "его",
    "ого",
    "ему",
    "ому",
    "иях",
    "иям",
    "ием",
    "ией",
    "ия",
    "ию",
    "ью",
    "ья",
    "ий",
    "ый",
    "ой",
    "ее",
    "ие",
    "ые",
    "ое",
    "ей",
    "ам",
    "ям",
    "ах",
    "ях",
    "ов",
    "ев",
    "ом",
    "ем",
    "а",
    "я",
    "ы",
    "и",
    "е",
    "у",
    "ю",
    "о",
  ];

  const matchedSuffix = suffixes.find((suffix) => token.endsWith(suffix));

  if (!matchedSuffix) {
    return token;
  }

  const stem = token.slice(0, -matchedSuffix.length);
  return stem.length >= 3 ? stem : token;
};

const normalizeSearchToken = (token: string): string => {
  if (/^[a-z0-9]+$/i.test(token)) {
    return stemEnglishToken(token);
  }

  if (/^[\p{Script=Cyrillic}\d]+$/u.test(token)) {
    return stemRussianToken(token);
  }

  return token;
};

const normalizeSearchText = (text: string): string =>
  tokenizeSearchText(text).map(normalizeSearchToken).join(" ");

export const useHelpSearch = ({
  searchIndex,
  minQueryLength = 2,
  maxResults = 20,
}: UseHelpSearchOptions) => {
  const { value, debouncedValue, setValue, clear, isPending } = useDebouncedSearchParam({
    paramName: "q",
    delay: 300,
  });

  const searchResults = useMemo(() => {
    if (!searchIndex || !debouncedValue || debouncedValue.length < minQueryLength) {
      return [];
    }

    const rawQuery = debouncedValue.toLowerCase().trim();
    const query = normalizeSearchText(debouncedValue).trim();
    const terms = query.split(/\s+/);

    const results: SearchResult[] = [];

    searchIndex
      .filter((item) => item.type === "article")
      .forEach((item) => {
        let score = 0;
        let matchType: "title" | "description" | "content" = "content";

        const titleLower = item.title.toLowerCase();
        const descriptionLower = item.description.toLowerCase();
        const normalizedTitle = normalizeSearchText(item.title);
        const normalizedDescription = normalizeSearchText(item.description);
        const normalizedContent = normalizeSearchText(item.content || "");

        if (titleLower.includes(rawQuery) || normalizedTitle.includes(query)) {
          score += 100;
          matchType = "title";
        }

        const titleMatchCount = terms.filter((term) => normalizedTitle.includes(term)).length;
        if (titleMatchCount > 0) {
          score += titleMatchCount * 50;
          if (matchType !== "title") matchType = "title";
        }

        if (descriptionLower.includes(rawQuery) || normalizedDescription.includes(query)) {
          score += 40;
          if (matchType === "content") matchType = "description";
        }

        const descriptionMatchCount = terms.filter((term) =>
          normalizedDescription.includes(term)
        ).length;
        if (descriptionMatchCount > 0) {
          score += descriptionMatchCount * 20;
          if (matchType === "content") matchType = "description";
        }

        if (normalizedContent) {
          const contentMatchCount = terms.filter((term) => normalizedContent.includes(term)).length;
          if (contentMatchCount > 0) {
            score += contentMatchCount * 10;
          }
        }

        if (
          titleLower.startsWith(rawQuery) ||
          titleLower.includes(` ${rawQuery}`) ||
          normalizedTitle.startsWith(query) ||
          normalizedTitle.includes(` ${query}`)
        ) {
          score += 30;
        }

        if (score > 0) {
          results.push({
            ...item,
            score,
            matchType,
          });
        }
      });

    return results
      .sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        return a.title.localeCompare(b.title);
      })
      .slice(0, maxResults);
  }, [searchIndex, debouncedValue, minQueryLength, maxResults]);

  const hasQuery = debouncedValue.length >= minQueryLength;
  const isSearching = isPending || (value.length >= minQueryLength && !hasQuery);

  return {
    query: value,
    debouncedQuery: debouncedValue,
    setQuery: setValue,
    clearQuery: clear,
    results: searchResults,
    hasQuery,
    isSearching,
    hasResults: searchResults.length > 0,
  };
};
